import React, { useState, useEffect, useCallback, useRef } from "react";
import { terminalAPI } from "../../services/api";
import { usePOSCart } from "../../context/POSContext";
import "./POSGiftCardPayment.css";

/**
 * GIFTCARD-FLOW-002 Path A terminal sequence — auto-dismiss interval.
 *
 * Per Zuri's visual spec
 * (docs/design/GIFTCARD-FLOW-002-design.md §Auto-Dismiss Timer):
 * staff under time pressure should not have to tap Done; the SUCCESS
 * terminal modal closes itself after 1500ms (eyes read "Sale complete"
 * + residual amount, workflow advances). The Done button is a manual
 * override.
 */
const PATH_A_AUTO_DISMISS_MS = 1500;

/**
 * GIFTCARD-FLOW-002 Path A — auto-print delay.
 *
 * The auto-print is fire-and-forget the moment the SUCCESS terminal
 * renders. The Visual DoD line item (auto-print fires within 200ms of
 * SUCCESS-state render) is met by triggering on render via useEffect.
 * The 100ms delay below is purely to let the receipt content render
 * into the DOM before the print window picks it up.
 */
const PATH_A_AUTO_PRINT_RENDER_DELAY_MS = 100;

/**
 * Extract last-8 of a Mongo ObjectId-shaped string for the Ref badge.
 *
 * Q6 LOCKED scheme: receipt + on-screen show `Ref: POS-{last-8-of-_id}`.
 * Defensive against non-string inputs and short strings.
 *
 * @param {unknown} id - Likely a 24-char hex ObjectId string
 * @returns {string} Last 8 characters, or the input coerced to string
 */
const lastEightOfId = (id) => {
  const s = id == null ? "" : String(id);
  return s.length > 8 ? s.slice(-8) : s;
};

/**
 * POSGiftCardPayment component for processing gift card payments.
 *
 * GIFTCARD-FLOW-002 (Path A success handoff): on full-coverage
 * redemption, the SUCCESS terminal renders Zuri's spec
 * (success icon, "Sale complete.", redemption + residual lines,
 * Q6 LOCKED Ref badge, "Done" CTA), auto-prints the single-tender
 * gift-card receipt, and auto-dismisses after 1.5s. Cart-clear is
 * invoked via the `onSuccess` callback which POSMain wires to
 * `clearCart` + `clearCustomer`.
 *
 * Invariant 4 (modal does NOT show success while persistence is
 * unresolved): the SUCCESS terminal renders only on backend 200 with
 * `redemptionId` populated. The deployed backend flips
 * `TerminalTransaction.status = "paid"` on full-coverage BEFORE
 * responding 200 (giftCardController.redeemGiftCard L154-163), so the
 * 200 is the durable-record signal.
 *
 * Invariant 5 (cart clears ONLY when `status === "paid"`): the FE keys
 * cart-clear off of the full-coverage branch — when the requested
 * redemption amount (cents) >= cart total (cents). Partial-coverage
 * redemptions (Path B, GIFTCARD-FLOW-003) do NOT trigger cart-clear:
 * `onSuccess` receives `isFullCoverage: false` and POSMain leaves the
 * cart untouched.
 *
 * @param {Object} props
 * @param {Function} props.onClose - Closes the modal
 * @param {Function} props.onSuccess - Receives the result payload incl.
 *   `isFullCoverage` so POSMain can decide whether to clear the cart
 */
const POSGiftCardPayment = ({ onClose, onSuccess }) => {
  const [giftCardCode, setGiftCardCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [giftCardInfo, setGiftCardInfo] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  // Tracks whether the redemption is full-coverage (Path A) vs partial
  // (Path B). Drives the SUCCESS terminal copy + the cart-clear signal.
  const [isFullCoverage, setIsFullCoverage] = useState(false);

  // Get cart context (items for receipt; totals for full-coverage check)
  const { items: cartItems, totals } = usePOSCart();

  // Refs for auto-print + auto-dismiss timers (cleanup on unmount)
  const autoDismissTimerRef = useRef(null);
  const autoPrintTimerRef = useRef(null);
  // Snapshot of cart items at success — receipt renders against this
  // even though the cart clears post-dismiss.
  const receiptItemsRef = useRef([]);
  const receiptTotalsRef = useRef(null);

  // Set default payment amount to cart total when gift card is validated
  useEffect(() => {
    if (giftCardInfo && giftCardInfo.balance > 0) {
      // Default to the cart total or the gift card balance, whichever is lower
      const defaultAmount = Math.min(totals.total, giftCardInfo.balance);
      setPaymentAmount((defaultAmount / 100).toFixed(2));
    }
  }, [giftCardInfo, totals.total]);

  // Handle gift card code validation
  const handleValidateGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setError("Please enter a gift card code");
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const response = await terminalAPI.validateGiftCard({
        code: giftCardCode.trim(),
      });

      if (response.data.success) {
        setGiftCardInfo({
          code: response.data.code,
          balance: response.data.balance,
          formattedBalance: response.data.formattedBalance,
          expiryDate: response.data.expiryDate,
          isExpired: response.data.isExpired,
        });
      } else {
        setError(response.data.error || "Invalid gift card");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.userMessage ||
          "Failed to validate gift card"
      );
    } finally {
      setIsValidating(false);
    }
  };

  // Handle gift card payment
  const handleProcessPayment = async () => {
    if (!giftCardInfo) {
      setError("Please validate a gift card first");
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid payment amount");
      return;
    }

    // Convert amount to cents
    const amountInCents = Math.round(amount * 100);

    if (amountInCents > giftCardInfo.balance) {
      setError("Payment amount exceeds gift card balance");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create a transaction for the gift card payment.
      // NOTE: must call createGiftCardTransaction (record-only,
      // /terminal/gift-card-transactions) — NOT createTerminalPayment
      // (Stripe-Terminal-coupled /terminal/process-payment). The latter
      // hard-requires payment_intent_id + reader_id which gift cards do
      // not have. PR #194 introduced the decoupled endpoint; this call
      // site must stay on the decoupled wrapper. See R-023-class log +
      // regression-guard test below.
      const transactionResponse = await terminalAPI.createGiftCardTransaction({
        amount: amountInCents,
        currency: "usd",
        paymentMethod: "gift_card",
        metadata: {
          giftCardCode: giftCardInfo.code,
        },
      });

      if (!transactionResponse.data.success) {
        throw new Error(
          transactionResponse.data.error || "Failed to create transaction"
        );
      }

      // Process the gift card redemption
      const redemptionResponse = await terminalAPI.redeemGiftCard({
        code: giftCardInfo.code,
        amount: amountInCents,
        transactionId: transactionResponse.data.transactionId,
      });

      if (redemptionResponse.data.success) {
        // Invariant 4: durable-record signal is the 200 with redemptionId
        // populated. The deployed redeemGiftCard handler flips
        // TerminalTransaction.status = "paid" BEFORE responding 200 on
        // full coverage (giftCardController L154-163). No optimistic UI:
        // we render the SUCCESS terminal only after the 200 lands.
        const fullCoverage = amountInCents >= totals.total;

        // Snapshot cart items + totals for the receipt — POSMain will
        // clear the live cart on dismiss/Done, so the receipt must
        // render off a snapshot taken HERE.
        receiptItemsRef.current = cartItems.slice();
        receiptTotalsRef.current = {
          subtotal: totals.subtotal,
          taxAmount: totals.taxAmount,
          total: totals.total,
        };

        setIsFullCoverage(fullCoverage);
        setPaymentResult({
          code: redemptionResponse.data.code,
          amount: redemptionResponse.data.amount,
          formattedAmount: redemptionResponse.data.formattedAmount,
          remainingBalance: redemptionResponse.data.remainingBalance,
          formattedRemainingBalance:
            redemptionResponse.data.formattedRemainingBalance,
          transactionId: redemptionResponse.data.transactionId,
          redemptionId: redemptionResponse.data.redemptionId,
        });
        setPaymentSuccess(true);
      } else {
        throw new Error(
          redemptionResponse.data.error || "Failed to redeem gift card"
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to process payment"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Trigger the auto-print receipt — single-tender gift-card layout
   * (Path A only). Fire-and-forget: opens a print window, writes the
   * receipt content, calls print(), closes. Mirrors POSPayment's
   * existing pattern so the printer behavior is consistent.
   */
  const triggerAutoPrintReceipt = useCallback(() => {
    if (typeof window === "undefined" || !window.open) {
      return;
    }
    const printContent = document.getElementById("pos-gift-card-receipt");
    if (!printContent) {
      return;
    }
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      return;
    }
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .pos-gc-receipt { width: 300px; margin: 0 auto; color: #1a1a1a; }
            .pos-gc-receipt-header { text-align: center; margin-bottom: 20px; }
            .pos-gc-receipt-header h3 { margin: 0 0 10px 0; }
            .pos-gc-receipt-header p { margin: 2px 0; }
            .pos-gc-receipt-date { margin-top: 10px; }
            .pos-gc-receipt-ref { font-family: monospace; font-size: 12px; margin-top: 6px; }
            .pos-gc-receipt-items { margin-bottom: 16px; }
            .pos-gc-receipt-items table { width: 100%; border-collapse: collapse; }
            .pos-gc-receipt-items th { border-bottom: 1px solid #ddd; text-align: left; padding: 5px; }
            .pos-gc-receipt-items td { padding: 5px; }
            .pos-gc-receipt-summary-row { display: flex; justify-content: space-between; margin: 4px 0; }
            .pos-gc-receipt-tender { display: flex; justify-content: space-between; margin: 6px 0; border-top: 1px solid #ddd; padding-top: 6px; }
            .pos-gc-receipt-total-due { display: flex; justify-content: space-between; font-weight: bold; font-size: 1.1em; margin-top: 8px; }
            .pos-gc-receipt-footer { text-align: center; margin-top: 16px; border-top: 1px solid #ddd; padding-top: 10px; }
            .pos-gc-receipt-residual { font-weight: bold; margin-bottom: 8px; }
          </style>
        </head>
        <body>
          ${printContent.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    // Slight delay matches POSPayment's existing print pattern.
    setTimeout(() => {
      try {
        printWindow.print();
        printWindow.close();
      } catch (_printErr) {
        // Fire-and-forget per Zuri's spec — print failure does NOT
        // block the SUCCESS terminal sequence. Staff can re-print via
        // the standard POS reprint affordance.
      }
    }, 250);
  }, []);

  /**
   * Complete the SUCCESS terminal sequence: invoke onSuccess (which
   * tells POSMain whether to clear the cart, Invariant 5), then close
   * the modal. Used by both the auto-dismiss timer and the Done button.
   */
  const completeSuccessSequence = useCallback(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
    if (autoPrintTimerRef.current) {
      clearTimeout(autoPrintTimerRef.current);
      autoPrintTimerRef.current = null;
    }
    if (onSuccess && paymentResult) {
      onSuccess({
        code: paymentResult.code,
        amount: paymentResult.amount,
        formattedAmount: paymentResult.formattedAmount,
        remainingBalance: paymentResult.remainingBalance,
        formattedRemainingBalance: paymentResult.formattedRemainingBalance,
        transactionId: paymentResult.transactionId,
        redemptionId: paymentResult.redemptionId,
        // Invariant 5: only full-coverage triggers the cart-clear at
        // POSMain. Partial coverage is Path B (GIFTCARD-FLOW-003).
        isFullCoverage,
      });
    }
    if (onClose) {
      onClose();
    }
  }, [onSuccess, onClose, paymentResult, isFullCoverage]);

  // Auto-print + auto-dismiss timers fire once the SUCCESS state renders
  useEffect(() => {
    if (!paymentSuccess || !isFullCoverage) {
      // Only Path A (full-coverage) gets the auto-print + 1.5s dismiss.
      // Partial-coverage (Path B) currently surfaces the legacy modal
      // close behavior — GIFTCARD-FLOW-003 will own Path B's modal copy.
      return undefined;
    }
    // Auto-print fires after a short delay so the receipt DOM is mounted.
    autoPrintTimerRef.current = setTimeout(() => {
      triggerAutoPrintReceipt();
    }, PATH_A_AUTO_PRINT_RENDER_DELAY_MS);
    // Auto-dismiss + cart-clear at 1.5s (Zuri spec §Auto-Dismiss Timer).
    autoDismissTimerRef.current = setTimeout(() => {
      completeSuccessSequence();
    }, PATH_A_AUTO_DISMISS_MS);
    return () => {
      if (autoPrintTimerRef.current) {
        clearTimeout(autoPrintTimerRef.current);
        autoPrintTimerRef.current = null;
      }
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
        autoDismissTimerRef.current = null;
      }
    };
  }, [
    paymentSuccess,
    isFullCoverage,
    completeSuccessSequence,
    triggerAutoPrintReceipt,
  ]);

  // Format price from cents to dollars
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Path A SUCCESS terminal: zero-residual case shows "Gift card fully
  // redeemed." instead of "$0.00 remains on this gift card." per Zuri.
  const isZeroResidual =
    paymentResult && paymentResult.remainingBalance === 0;
  const residualLine = isZeroResidual
    ? "Gift card fully redeemed."
    : paymentResult
      ? `${paymentResult.formattedRemainingBalance} remains on this gift card.`
      : "";

  const refIdShort = paymentResult
    ? lastEightOfId(paymentResult.transactionId)
    : "";
  const refLabel = `Ref: POS-${refIdShort}`;

  // Path A SUCCESS terminal — Zuri's spec
  const renderPathASuccessTerminal = () => (
    <div
      className="pos-gift-card-success pos-gift-card-success--path-a"
      role="status"
      aria-live="polite"
    >
      <div
        className="pos-gift-card-success-icon"
        aria-label="Sale complete"
      >
        <i className="fas fa-check-circle"></i>
      </div>
      <h3 className="pos-gift-card-success-title">Sale complete.</h3>
      <div className="pos-gift-card-success-redemption">
        {paymentResult.formattedAmount} redeemed.
      </div>
      <div className="pos-gift-card-success-residual">{residualLine}</div>
      <div
        className="pos-gift-card-success-ref"
        aria-label={`Transaction reference POS-${refIdShort}`}
      >
        {refLabel}
      </div>
      <button
        className="pos-gift-card-done-btn"
        onClick={completeSuccessSequence}
        aria-label="Done — close success and return to next sale"
      >
        Done
      </button>
      {/* Hidden printable receipt — picked up by triggerAutoPrintReceipt.
          Single-tender gift-card layout per Zuri's spec. Rendered off
          the cart snapshot taken at redeem-success (receiptItemsRef)
          because POSMain clears the live cart on dismiss. */}
      <div
        id="pos-gift-card-receipt"
        className="pos-gc-receipt"
        style={{ display: "none" }}
        aria-hidden="true"
      >
        <div className="pos-gc-receipt-header">
          <h3>Diviti Adora Infrared &amp; Red Light Studio</h3>
          <p>1930 E Tangerine Rd Ste 100</p>
          <p>Oro Valley, AZ 85755</p>
          <div className="pos-gc-receipt-date">
            <p>
              {new Date().toLocaleDateString("en-US", {
                timeZone: "America/Phoenix",
              })}
            </p>
            <p>
              {new Date().toLocaleTimeString("en-US", {
                timeZone: "America/Phoenix",
              })}{" "}
              MST
            </p>
          </div>
          <p className="pos-gc-receipt-ref">{refLabel}</p>
        </div>
        <div className="pos-gc-receipt-items">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {receiptItemsRef.current.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.name}</td>
                  <td>{item.quantity}</td>
                  <td>{formatPrice(item.totalPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {receiptTotalsRef.current && (
          <>
            <div className="pos-gc-receipt-summary-row">
              <span>Subtotal:</span>
              <span>{formatPrice(receiptTotalsRef.current.subtotal)}</span>
            </div>
            <div className="pos-gc-receipt-summary-row">
              <span>Tax:</span>
              <span>{formatPrice(receiptTotalsRef.current.taxAmount)}</span>
            </div>
            <div className="pos-gc-receipt-summary-row">
              <span>Total:</span>
              <span>{formatPrice(receiptTotalsRef.current.total)}</span>
            </div>
          </>
        )}
        <div className="pos-gc-receipt-tender">
          <span>Gift Card ****{(paymentResult.code || "").slice(-4)}</span>
          <span>-{paymentResult.formattedAmount}</span>
        </div>
        <div className="pos-gc-receipt-total-due">
          <span>Total Due:</span>
          <span>$0.00</span>
        </div>
        <div className="pos-gc-receipt-footer">
          {!isZeroResidual && (
            <p className="pos-gc-receipt-residual">
              Gift card residual: {paymentResult.formattedRemainingBalance}
            </p>
          )}
          <p>Thank you for visiting Diviti Adora Studio!</p>
        </div>
      </div>
    </div>
  );

  // Legacy SUCCESS panel for partial-coverage (Path B) — preserved
  // until GIFTCARD-FLOW-003 ships its own Path B terminal sequence.
  // No regressions to partial-coverage behavior in this story.
  const renderLegacySuccess = () => (
    <div className="pos-gift-card-success">
      <div className="pos-gift-card-success-icon">
        <i className="fas fa-check-circle"></i>
      </div>
      <h3>Payment Successful!</h3>
      <div className="pos-gift-card-success-details">
        <p>
          <strong>Amount:</strong> {paymentResult.formattedAmount}
        </p>
        <p>
          <strong>Gift Card:</strong> {paymentResult.code}
        </p>
        <p>
          <strong>Remaining Balance:</strong>{" "}
          {paymentResult.formattedRemainingBalance}
        </p>
      </div>
      <button
        className="pos-gift-card-done-btn"
        onClick={completeSuccessSequence}
      >
        Done
      </button>
    </div>
  );

  return (
    <div className="pos-modal-overlay">
      <div className="pos-gift-card-modal">
        <div className="pos-gift-card-header">
          <h2>Gift Card Payment</h2>
          <button className="pos-gift-card-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {paymentSuccess ? (
          isFullCoverage ? (
            renderPathASuccessTerminal()
          ) : (
            renderLegacySuccess()
          )
        ) : (
          <div className="pos-gift-card-content">
            {error && <div className="pos-gift-card-error">{error}</div>}

            {!giftCardInfo ? (
              <div className="pos-gift-card-validation">
                <p>Enter your gift card code to proceed with payment.</p>
                <div className="pos-gift-card-input-group">
                  <input
                    type="text"
                    placeholder="Gift Card Code"
                    value={giftCardCode}
                    onChange={(e) => setGiftCardCode(e.target.value)}
                    disabled={isValidating}
                    className="pos-gift-card-input"
                  />
                  <button
                    className="pos-gift-card-validate-btn"
                    onClick={handleValidateGiftCard}
                    disabled={isValidating || !giftCardCode.trim()}
                  >
                    {isValidating ? "Validating..." : "Validate"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="pos-gift-card-payment">
                <div className="pos-gift-card-info">
                  <p>
                    <strong>Gift Card:</strong> {giftCardInfo.code}
                  </p>
                  <p>
                    <strong>Available Balance:</strong>{" "}
                    {giftCardInfo.formattedBalance}
                  </p>
                  {giftCardInfo.expiryDate && (
                    <p>
                      <strong>Expires:</strong>{" "}
                      {new Date(giftCardInfo.expiryDate).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div className="pos-gift-card-cart-total">
                  <p>
                    <strong>Cart Total:</strong> {formatPrice(totals.total)}
                  </p>
                </div>

                <div className="pos-gift-card-amount">
                  <label htmlFor="paymentAmount">Payment Amount:</label>
                  <div className="pos-gift-card-amount-input">
                    <span className="pos-gift-card-currency">$</span>
                    <input
                      id="paymentAmount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={(giftCardInfo.balance / 100).toFixed(2)}
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      disabled={isProcessing}
                      className="pos-gift-card-amount-input"
                    />
                  </div>
                </div>

                <div className="pos-gift-card-actions">
                  <button
                    className="pos-gift-card-cancel-btn"
                    onClick={onClose}
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                  <button
                    className="pos-gift-card-process-btn"
                    onClick={handleProcessPayment}
                    disabled={
                      isProcessing ||
                      !paymentAmount ||
                      parseFloat(paymentAmount) <= 0 ||
                      parseFloat(paymentAmount) * 100 > giftCardInfo.balance
                    }
                  >
                    {isProcessing ? "Processing..." : "Process Payment"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { lastEightOfId, PATH_A_AUTO_DISMISS_MS };
export default POSGiftCardPayment;
